import { useTheme } from "@mui/styles";
import {
  SEARCH_TYPE_CARE,
  SEARCH_TYPE_HOME_CARE,
  SEARCH_TYPE_HOSPITAL,
  SEARCH_TYPE_MEDICAL_SUPPLIES,
  SEARCH_TYPE_PSYCHIATRIC_CARE,
  SEARCH_TYPE_REHABILITATION,
} from "core/consts";
import { AuctionNoPatient, SearchType } from "core/types";
import { DotWithBorder } from "ds_legacy/components/Dot";
import RSButton from "ds_legacy/components/RSButton";
import { HorizontalLayout } from "ds_legacy/materials/layouts";
import { border, margin } from "ds_legacy/materials/metrics";
import { FONT_WEIGHT_BOLD } from "ds_legacy/materials/typography";
import { useCareseekerNavigationHandlers } from "dsl/hooks";
import { PRODUCT_TOURS, tourAttributes } from "dsl/molecules/useProductTour";
import styled from "styled-components";
import { useTranslations } from "translations";
import Translations from "translations/types";

export type PatientTabsProps = {
  auctionId: number | undefined;
  auctions: Array<AuctionNoPatient> | undefined;
};

const DotContainer = styled.span`
  display: flex;
  justify-content: center;
  margin: ${margin(0, 1)};
`;

const TabContainer = styled.div<{ isActive: boolean }>`
  border-bottom: ${(props) =>
    props.isActive
      ? border({
          color: (props.theme as { palette: { primary: { main: string } } })
            .palette.primary.main,
          width: 2,
        })
      : "none"};
  margin: ${margin(0, 1)};
  white-space: nowrap;
`;

function getTabName(
  searchType: SearchType,
  translations: Translations,
): string {
  switch (searchType) {
    case SEARCH_TYPE_CARE:
      return translations.ontologies.patientType.values.care;
    case SEARCH_TYPE_HOSPITAL:
      return translations.ontologies.patientType.values.hospital;
    case SEARCH_TYPE_REHABILITATION:
      return translations.ontologies.patientType.values.rehab;
    case SEARCH_TYPE_MEDICAL_SUPPLIES:
      return translations.ontologies.patientType.values.medicalSupplies;
    case SEARCH_TYPE_HOME_CARE:
      return translations.ontologies.patientType.values.homeCare;
    case SEARCH_TYPE_PSYCHIATRIC_CARE:
      return translations.ontologies.patientType.values.psychiatricCare;
    default:
      return translations.ontologies.patientType.values.care;
  }
}

export function PatientTabs({ auctionId, auctions }: PatientTabsProps) {
  const translations = useTranslations();
  const theme = useTheme();
  const navigationHandlers = useCareseekerNavigationHandlers();
  return (
    <HorizontalLayout
      aligned
      {...tourAttributes({
        stepKey: PRODUCT_TOURS.parallel_search.steps.old_search_tab.key,
        tourKey: PRODUCT_TOURS.parallel_search.key,
      })}
    >
      {auctions?.map((auction) => {
        const isActive = auction.id === auctionId;
        const hasStatusDot = !!auction.new_responses;
        const tabName = getTabName(auction.search_type, translations);
        const tabTitle = auction.profile?.has_transitional_care
          ? translations.patientForms.transitionalCareForm.tabTitle({
              tabName,
            })
          : tabName;

        return (
          <TabContainer key={auction.id} isActive={isActive}>
            <RSButton
              color={isActive ? "primary" : "grey"}
              id={getTabName(auction.search_type, translations)}
              loading="na"
              style={{
                backgroundColor: "transparent",
                fontWeight: isActive ? FONT_WEIGHT_BOLD : undefined,
                margin: margin(0.25, 1),
              }}
              variant="text"
              onClick={() =>
                navigationHandlers.patient.goToAuction({
                  auctionId: auction.id,
                })
              }
            >
              {hasStatusDot ? (
                <DotContainer>
                  {tabTitle}
                  <DotWithBorder color={theme.palette.secondary.main} />
                </DotContainer>
              ) : (
                tabTitle
              )}
            </RSButton>
          </TabContainer>
        );
      })}
    </HorizontalLayout>
  );
}
